import { PatientListItem } from '@/api'
import { ReactComponent as CloseIcon } from '@/assets/close.svg'
import { ReactComponent as InfoIcon } from '@/assets/info.svg'
import { HorizontalScrollerWithDots } from '@/components'
import { useAuthContext } from '@/providers'
import { createPortal } from 'react-dom'
import { Trans } from 'react-i18next'

type DiaryHelpModalProps =
  | { onClose: () => void; isIcon?: false; onOpen?: never; isOpen?: never }
  | { onClose: () => void; isIcon: true; onOpen: () => void; isOpen: boolean }

export const DiaryHelpModal = (props: DiaryHelpModalProps) => {
  const { currentPatient } = useAuthContext()

  const isOpen = props.isIcon ? props.isOpen : true
  const onClose = props.onClose
  const onOpen = props.isIcon ? props.onOpen : undefined

  // Icon-only mode: render the info icon that triggers the modal
  if (props.isIcon) {
    return (
      <>
        <div className="flex justify-end w-full">
          <InfoIcon
            fill="var(--b50)"
            height={20}
            width={20}
            className="cursor-pointer"
            onClick={onOpen}
          />
        </div>
        {isOpen && <DiaryHelpContent onClose={onClose} currentPatient={currentPatient} />}
      </>
    )
  }

  // Direct modal mode: render modal directly
  return <DiaryHelpContent onClose={onClose} currentPatient={currentPatient} />
}

const DiaryHelpContent = ({
  onClose,
  currentPatient,
}: {
  onClose: () => void
  currentPatient?: PatientListItem
}) =>
  createPortal(
    <div className="fixed top-0 bottom-0 left-0 right-0 animate-slide-up flex justify-center bg-sec0 z-[10000] pt-[60px] overflow-y-auto">
      <div className="subpage-header">
        <CloseIcon
          height={24}
          width={24}
          fill="var(--primary)"
          className="cursor-pointer"
          onClick={onClose}
        />
        Hilfe
        <div />
      </div>
      <div className="max-w-[488px] flex flex-col gap-6 py-4 sm:p-4 w-full overflow-y-auto text-b50">
        <div className="text-h2reg text-b100">
          <Trans i18nKey="icuDiaryPage.help.title" className="text-h2" />
        </div>
        <Trans
          i18nKey="icuDiaryPage.help.text"
          values={{
            patientName: `${currentPatient?.first_name} ${currentPatient?.last_name}`,
            patientFirstName: currentPatient?.first_name,
          }}
        />

        {/* Additional info about shared entries */}
        <div className="flex flex-col gap-3 mt-2">
          <div className="text-h4 text-b100">Wer schreibt Einträge?</div>
          <div className="text-textS text-b50 leading-relaxed">
            Sowohl Angehörige als auch das Behandlungsteam können Einträge verfassen. Die Einträge
            des Behandlungsteams sind für alle sichtbar – Ihre persönlichen Einträge kann das
            Behandlungsteam nicht sehen. Gemeinsam ergeben sie eine chronologische Dokumentation des
            Aufenthalts.
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="text-h4 text-b100">Nach der Entlassung</div>
          <div className="text-textS text-b50 leading-relaxed">
            Nach der Entlassung bleibt das Tagebuch als Archiv verfügbar. Sie können es jederzeit
            wieder lesen. Es kann bei der Verarbeitung der Erfahrungen helfen.
          </div>
        </div>

        {/* Example entries */}
        <div className="flex flex-col gap-3">
          <div className="text-h4 text-b100">Beispieleinträge</div>
          <HorizontalScrollerWithDots
            gap={12}
            count={3}
            containerClassName="sm:max-h-[50vh]"
            style={{ fontStyle: 'italic' }}
          >
            <div className="w-[100%] shrink-0">
              Mein Liebster, <br />
              es ist alles so anders. Du fehlst mir und ich habe Angst um Dich. Du bist ganz warm,
              wenn ich Deine Hand halte, aber Deine Seele ist weit fern. Komm zurück.
              <br />
              <br />
              Doris
            </div>
            <div className="w-[100%] shrink-0">
              Lieber Papa, <br />
              heute haben Louise und Dennis Bilder für Dich mitgegeben, die haben wir im Zimmer
              aufgehängt! Die Ärzte sagen, dass das letzte Kopfbild ganz gut aussehen würde. Ich
              glaube Du bist hier ganz gut aufgehoben.
              <br />
              <br />
              Hab Dich lieb! <br />
              Sabine
            </div>
            <div className="w-[100%] shrink-0">
              Liebster Knut, <br />
              was für ein toller Tag, heute hast Du mich angeschaut und gelächelt!!! Ich glaube,
              dass wir das Schlimmste hinter uns haben und ich bin so erleichtert!
              <br />
              <br />
              Deine Doris
            </div>
          </HorizontalScrollerWithDots>
        </div>
      </div>
    </div>,
    document.body,
  )
